<template>
  <div class='issues-layout'>
    <div class="issues-filters-header">
      <advanced-filters-header
        layout='issues'
        @applyDomains='apply'
        @applyOrganizations='() => {}'
      />
      <centralized-issues-filters-header
        v-if='searchFlag'
        :selected-domains='Array.from(domainToPass)'
        @loadIssues='loadIssues'
      />
    </div>
    <centralized-issues-data-view
      :issues-list='issuesList'
      :loading='displayLoadingAnimation'
      @columnSorted='columnSorted'
      @changedSelectedPage='changedSelectedPage'
      @changedItemsAmountPerPage='changedItemsAmountPerPage'
      @downloadCSVReport='downloadCSVReport'
      @loadIssues='loadIssues'
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS,
  LOAD_DOMAINS_FROM_CONFIGURATION,
  SET_LOADING_DATA_FLAG,
} from '@/store/project/actionTypes';
import { LOAD_DOMAINS } from '@/store/user/actionTypes';
import { userRoleGroups as USER_ROLE_GROUPS } from '@/constants/auth';
import { CLEAR_CENTRALIZED_ISSUES_PAGINATION_STATE, UPDATE_ITEMS_PER_PAGE } from '@/store/pagination/actionTypes';
import {
  CHANGE_CENTRALIZES_ISSUES_LIST_OPTIONS,
} from '@/store/centralizedIssues/actionTypes';
import { downloadIssuesCSVReport } from '@/services/repository';
import CentralizedIssuesFiltersHeader from '@/components/CentralizedIssues/CentralizedIssuesFiltersHeader.vue';
import CentralizedIssuesDataView from '@/components/CentralizedIssues/CentralizedIssuesDataView.vue';
import AdvancedFiltersHeader from '@/components/common/AdvancedFiltersHeader.vue';
import { CLEAR_DOMAIN_FILTER } from '@/store/filters/actionTypes';
import { rules } from '@/utils/validationUtils';
import { issueStatusKey } from '@/utils/suppression';

export default {
  name: 'IssuesLayout',
  components: {
    AdvancedFiltersHeader,
    CentralizedIssuesDataView,
    CentralizedIssuesFiltersHeader,
  },
  data() {
    return {
      polling: null,
      page: 1,
      searchFlag: false,
    };
  },
  async mounted() {
    await this.init();
  },
  watch: {
    async organizationFilter() {
      await this.$store.dispatch(CLEAR_DOMAIN_FILTER);
    },
    async profile() {
      if (this.profile) {
        await this.$store.dispatch(LOAD_DOMAINS);
        await this.$store.dispatch(LOAD_DOMAINS_FROM_CONFIGURATION);
        await this.$store.dispatch(CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS, {
          noLimit: true,
        });
      }
    },
    async userRoleGroup() {
      if (this.userRoleGroup && this.userRoleGroup === USER_ROLE_GROUPS.CISO) {
        await this.$store.dispatch(LOAD_DOMAINS);
        await this.$store.dispatch(LOAD_DOMAINS_FROM_CONFIGURATION);
        await this.$store.dispatch(CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS, {
          noLimit: true,
        });
        if (!this.searchFlag) {
          await this.init();
        }
      }
    },
  },
  beforeDestroy() {
    clearInterval(this.polling);
  },
  async created() {
    await this.$store.dispatch(CLEAR_CENTRALIZED_ISSUES_PAGINATION_STATE);
    if (this.profile) {
      await this.$store.dispatch(LOAD_DOMAINS);
      await this.$store.dispatch(LOAD_DOMAINS_FROM_CONFIGURATION);
      await this.$store.dispatch(CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS, {
        noLimit: true,
      });
    }
  },
  computed: {
    ...mapGetters([
      'centralizedIssuesListOptions',
      'centralizedIssuesListSort',
      'centralizedIssuesPaginationState',
      'centralizedIssues',
      'centralizedIssuesListLoading',
      'domains',
      'configurationDomains',
      'domainFilter',
      'userRoleGroup',
      'detailedIssueForCentralizedView',
      'paginationItemsPerPageByTabs',
      'profile',
      'organizationFilter',
      'issuesRefId',
      'issuesRunningInterval',
    ]),
    displayLoadingAnimation() {
      return !this.centralizedIssuesListLoading;
    },
    issuesList() {
      return this.centralizedIssues.map(issue => ({
        id: issue.id,
        date: issue.createdDate,
        name: issue.name,
        displayName: issue.displayName,
        tld: issue.tld,
        asset: this.formatAssetName(issue),
        description: issue.metaData.description || 'N/A',
        priority: issue.priority,
        severity: issue.severity,
        probability: issue.probability,
        status: issueStatusKey(issue),
        issueStatus: issue.status,
        suppression: issue.suppression || null,
        checksum: issue.checksum,
        cycles: issue.issueCount,
        firstSeen: issue.firstDate,
        lastSeen: issue.lastDate,
        metaData: issue.metaData,
      }));
    },
    itemsPerPage() {
      return this.paginationItemsPerPageByTabs.centralizedIssues;
    },
    pages() {
      return this.centralizedIssuesPaginationState?.pageCount || Math.ceil(this.issuesList.length / this.itemsPerPage);
    },
    listOfDomains() {
      const domainsSet = new Set(this.domains);
      this.configurationDomains.forEach(configurationDomain => domainsSet.add(configurationDomain.configData));
      return Array.from(domainsSet);
    },
    domainSelected: {
      get() {
        return this.domainFilter;
      },
    },
    domainToPass() {
      return this.domainSelected.length > 0 ? this.domainSelected : this.domains;
    },
  },
  methods: {
    formatAssetName(issue) {
      const assetsPart = issue.asset.split(`.${issue.tld}`);
      return rules.virtualSubdomain(issue.asset) ? assetsPart[0] : issue.asset;
    },
    async init() {
      let search = '';
      let specificSearch = false;
      let issueTypes = [];
      if (this.userRoleGroup === USER_ROLE_GROUPS.CISO) {
        if (this.$route.params.search && this.$route.params.search !== '') {
          search = this.$route.params.search;
        }
        if (this.$route.params.tld && this.$route.params.tld.length > 0) {
          if (this.$route.params.specificSearch && this.$route.params.specificSearch.length > 0) {
            specificSearch = this.$route.params.specificSearch;
            search = this.$route.params.tld;
          }
        }
        if (this.$route.params.issueTypes && this.$route.params.issueTypes.length > 0) {
          issueTypes = this.$route.params.issueTypes;
        }
        await this.loadIssues({
          page: 1,
          limit: this.itemsPerPage,
          domain: this.domainToPass,
          specificSearch,
          turnOnLoading: true,
          hideSuppress: true,
          hideFixed: true,
          onlyCVEs: false,
          search,
          issueTypes,
        }, true);
        this.searchFlag = true;
      }
    },
    async changedSelectedPage(currentPage) {
      window.scrollTo(0, 0);
      this.page = currentPage;
      await this.loadIssues({
        page: this.page,
        domain: this.domainToPass,
        specificSearch: this.specificSearch,
        turnOnLoading: true,
      });
    },
    async changedItemsAmountPerPage(itemsPerPage) {
      window.scrollTo(0, 0);
      this.page = 1;
      await this.$store.dispatch(UPDATE_ITEMS_PER_PAGE, { itemsPerPage, tab: 'centralizedIssues' });
      await this.loadIssues({
        domain: this.domainToPass,
        specificSearch: this.specificSearch,
        limit: itemsPerPage,
        page: this.page,
        turnOnLoading: true,
      });
    },
    async downloadCSVReport() {
      const payload = {
        domain: this.domainToPass,
        search: this.centralizedIssuesListOptions.search,
        specificSearch: this.centralizedIssuesListOptions.toggles.specificSearch,
        onlyCVEs: this.centralizedIssuesListOptions.toggles.onlyCVEs,
        hideSuppress: this.centralizedIssuesListOptions.toggles.hideSuppress,
        hideFixed: this.centralizedIssuesListOptions.toggles.hideFixed,
      };
      const dataStream = await downloadIssuesCSVReport(payload);
      const blob = new Blob([dataStream], { type: 'text/plain' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute('download', `${Date.now()}-issues.csv`);
      document.body.appendChild(link);
      link.click();
    },
    async columnSorted({ column, direction }) {
      await this.loadIssues({
        page: this.page,
        limit: this.itemsPerPage,
        domain: this.domainToPass,
        specificSearch: this.specificSearch,
        search: this.centralizedIssuesListOptions.search,
        sort: direction
          ? {
            [column.sortingField || column.field]: direction,
          }
          : {},
      });
    },
    async apply() {
      if (this.domainSelected.length > 0) {
        await this.$store.dispatch(SET_LOADING_DATA_FLAG, true);
        await this.$store.dispatch(CLEAR_CENTRALIZED_ISSUES_PAGINATION_STATE);
        await this.loadIssues();
      }
    },
    clearInterval() {
      clearInterval(this.polling);
      this.polling = null;
    },
    async loadIssues(payload = {}, turnOnLoading = true) {
      this.clearInterval();
      await this.$store.dispatch(CHANGE_CENTRALIZES_ISSUES_LIST_OPTIONS, {
        page: this.page,
        limit: this.itemsPerPage,
        domain: this.domainToPass,
        specificSearch: this.specificSearch,
        turnOnLoading,
        issueTypes: [],
        ...payload,
      });
      if (this.issuesRefId && this.issuesRefId !== '') {
        this.polling = setInterval(async () => {
          if (!this.issuesRefId || this.issuesRefId === '') {
            this.clearInterval();
          } else if (!this.issuesRunningInterval) {
            await this.$store.dispatch(CHANGE_CENTRALIZES_ISSUES_LIST_OPTIONS, {
              page: this.page,
              limit: this.itemsPerPage,
              domain: this.domainToPass,
              specificSearch: this.specificSearch,
              turnOnLoading,
              issueTypes: [],
              refId: this.issuesRefId,
              ...payload,
            });
          }
        }, 3000);
      }
    },
    dispatch(e) {
      this.$emit('input', e);
      this.domainSelected = e;
    },
  },
};
</script>

<style lang='scss' scoped>
@import "~@/assets/styles/design-system";

.issues-layout {
  .issues-tab-loading {
    align-self: center;
    justify-self: center;
    margin-top: 20%;
  }
  .issues-filters-header {
    display: flex;
    flex-direction: row;
    margin-left: $space-8;
    margin-bottom: $space-4;

    :deep(.centralized-issues-filters-header) {
      margin-bottom: 0;
      // Offset to align with select input boxes (accounting for select title labels above)
      margin-top: rem(30);
    }

    :deep(.issue-search) {
      min-width: rem(400);
      margin-left: $space-8;
      margin-bottom: $space-10;
    }

    :deep(.filter-switch) {
      margin-left: $space-10;
      margin-bottom: $space-10;
    }
  }
  .counter {
    padding-left: $space-8;
    margin-top: -$space-8;
    margin-bottom: $space-4;
    font-family: $font-family-regular;
    font-size: $font-size-sm;
    text-transform: uppercase;
    color: var(--text-secondary);
  }
  .issues-list-header {
    display: flex;
    justify-content: space-between;
    padding-left: $space-8;
    padding-top: $space-8;
    margin-bottom: $space-8;
    padding-right: $space-8;
    .download-csv-btn {
      height: $space-8;
      margin-right: $space-6;
      &:hover {
        cursor: pointer;
      }
    }
    .title {
      color: var(--text-primary);
      font-family: $font-family-bold;
      font-size: $font-size-2xl;
      font-weight: bold;
      line-height: $line-height-tight;
      letter-spacing: 0;
    }
    .actions {
      display: flex;
      flex-direction: row;
    }
    .priority-sort {
      border: solid 1px transparent;
      border-radius: $radius-md;
      height: $space-8;
      padding: $space-0-5 $space-6 $space-0-5 $space-0-5;

      &:hover {
        cursor: pointer;
        background-color: var(--bg-tertiary);
      }
    }
  }
  .issues-list-content {
    &__footer {
      margin-top: $space-6;
      margin-bottom: $space-12;
    }
    &.loading {
      display: none;
    }
  }
}
</style>

<style lang='scss'>
@import "~@/assets/styles/design-system";

.issues-layout {
  .search-animation-wrapper {
    margin-top: -$space-1;
    &:hover {
      cursor: pointer;
    }
  }
  .search-animation {
    height: rem(30);
  }
  .probability {
    line-height: unset;
  }
  .severity {
    line-height: unset;
  }
  .wombat-table {
    --displayactioncell: none;
    padding: $space-4 0;
    .wombat-header {
      padding-bottom: $space-2;
    }
    .wombat-row.wombat-row {
      border-top: none;
      border-left: none;
      border-right: none;
    }
    .wombat-overlay {
      border-bottom: none;
      &:hover {
        background-color: var(--sidebar-active-bg);
        --display: block;
        --bg: linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 75%, var(--bg-tertiary) 100%);
        --displayactioncell: flex;
      }
    }
  }
}
.issue-search {
  .v-text-field.v-text-field--solo:not(.v-text-field--solo-flat) > .v-input__control > .v-input__slot {
    box-shadow: none;
    border: $border-width-thin solid var(--border-primary);
    border-radius: $radius-sm;
  }
  .v-input__icon.v-input__icon--append.v-input__icon--append {
    margin-top: 0;
  }
  .v-text-field.v-text-field--solo .v-input__control {
    min-height: $input-height-md;
  }
}
</style>
